/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// UI lib components
import { notifications } from '@mantine/notifications';

// Lib
import { apiClient } from '@lib/api-client';
import { TMutationConfig } from '@lib/react-query';

// Types
import {
	TBodyBrandKpis,
	TApiResponseBrandKpis,
} from '../types';

// Types
import { TApiError } from '@global-types/api/api';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const statisticsBrandKpis = (
	body: TBodyBrandKpis
): Promise<TApiResponseBrandKpis> => {
	return apiClient.post('/statistics/kpis/brands', {
		...body,
		withCredentials: true,
	});
};

type UseBrandKpisOptions = {
	mutationConfig?: TMutationConfig<
		typeof statisticsBrandKpis
	>;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useBrandKpis = ({
	mutationConfig,
}: UseBrandKpisOptions) => {
	const { t } = useTranslation();

	const { onSuccess, ...restConfig } =
		mutationConfig || {};

	return useMutation({
		mutationFn: statisticsBrandKpis,
		mutationKey: [queryKeys.app.dashboard.kpis.key],
		onSuccess: (...args) => {
			onSuccess?.(...args);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});
};
